import { CKEditor } from '@ckeditor/ckeditor5-react'
import { useToastAlertContext } from '@procore/toast-alert'
import { ClassicEditor, FileRepository, type EventInfo } from 'ckeditor5'
import React from 'react'

import { Toast, useI18nContext, useZIndexContext } from '@procore/core-react'
import { TextEditorOutput } from '../TextEditorOutput'
import { sanitizeTextEditorHtml } from '../TextEditorOutput/TextEditorOutput.utils'
import { debounce } from '../utils/debounce'
import { useStickyToolbar } from './StickyToolbar'
import {
  GlobalEditorStyles,
  StyledImageUploadWarningToast,
  StyledImageUploadWarningToastIcon,
  StyledTextEditor,
} from './TextEditor.styles'
import type { KeyDownListener, TextEditorProps } from './TextEditor.types'
import { TextEditorContext } from './TextEditorProvider'
import { TextEditorTheme } from './textEditorTheming'
import { useCKEditorCss } from './useCKEditorCss'
import { useCKEditorFocusScope } from './useCKEditorFocusScope'
import { useTabAsNavigation } from './useTabAsNavigation'
import { addButtonDataAttributes, getDefaultConfig } from './utils'
import { richTextEditorUploadHandler } from './utils/richTextEditorHandlers'

const DEBOUNCE_MS = 300

export const CKTextEditor = (props: Readonly<TextEditorProps>) => {
  const {
    disabled,
    error,
    readonly,
    value,
    initialValue,
    onChange,
    onInit,
    onBlur,
    onKeyDown,
    locale: propLocale,
    onDirty,
    companyId,
    projectId,
    toolName,
    placeholder,
    rows,
    extendable,
    ...restProps
  } = props
  const ariaLabel = props['aria-label']
  const ariaDescription = props['aria-description']
  const { isLoading: cssLoading } = useCKEditorCss()
  useCKEditorFocusScope()

  const editorRef = React.useRef<HTMLDivElement>(null)
  const editorInstanceRef = React.useRef<ClassicEditor | null>(null)
  const initialValueRef = React.useRef<string>(
    sanitizeTextEditorHtml(value || initialValue || '')
  )
  const lastExternalValueRef = React.useRef<string>(
    sanitizeTextEditorHtml(value || initialValue || '')
  )
  const keyDownListenerRef = React.useRef<KeyDownListener | null>(null)
  const [isDirtyState, setIsDirtyState] = React.useState(false)
  const [isEditorReady, setIsEditorReady] = React.useState(false)
  const i18n = useI18nContext()
  const { showToast } = useToastAlertContext()
  const { locale: contextLocale } = i18n
  const locale = propLocale || contextLocale

  const showImageUploadWarning = React.useCallback(
    (message: string) => {
      showToast.custom(({ toastDismissProps }) => (
        <StyledImageUploadWarningToast alignItems="center" padding="md xl">
          <StyledImageUploadWarningToastIcon size="lg" />
          <Toast.Text>{message}</Toast.Text>
          <Toast.Dismiss
            aria-label={i18n.t('textEditor.imageUpload.dismissWarning')}
            {...toastDismissProps}
          />
        </StyledImageUploadWarningToast>
      ))
    },
    [i18n, showToast]
  )

  React.useEffect(() => {
    if (value !== undefined) {
      lastExternalValueRef.current = sanitizeTextEditorHtml(value)
    }
  }, [value])

  React.useEffect(() => {
    const editor = editorInstanceRef.current
    if (!isEditorReady || !editor || value === undefined) return
    if (editor.editing.view.document.isFocused) return
    if (value === editor.getData()) return

    editor.data.set(sanitizeTextEditorHtml(value))
  }, [value, isEditorReady])

  // Cleanup keydown listener on unmount
  React.useEffect(() => {
    return () => {
      if (keyDownListenerRef.current && editorInstanceRef.current) {
        editorInstanceRef.current.editing.view.document.off(
          'keydown',
          keyDownListenerRef.current
        )
        keyDownListenerRef.current = null
      }
    }
  }, [])

  // Update aria-label when ariaLabel prop changes
  React.useEffect(() => {
    if (!isEditorReady || !editorInstanceRef.current) {
      return
    }

    const editor = editorInstanceRef.current
    editor.editing.view.change((writer) => {
      const viewEditableRoot = editor.editing.view.document.getRoot()
      if (viewEditableRoot) {
        if (ariaLabel) {
          writer.setAttribute('aria-label', ariaLabel, viewEditableRoot)
        } else {
          writer.removeAttribute('aria-label', viewEditableRoot)
        }
      }
    })
  }, [ariaLabel, isEditorReady])

  // Update aria-description when ariaDescription prop changes
  React.useEffect(() => {
    if (!isEditorReady || !editorInstanceRef.current) {
      return
    }

    const editor = editorInstanceRef.current
    editor.editing.view.change((writer) => {
      const viewEditableRoot = editor.editing.view.document.getRoot()
      if (viewEditableRoot) {
        if (ariaDescription) {
          writer.setAttribute(
            'aria-description',
            ariaDescription,
            viewEditableRoot
          )
        } else {
          writer.removeAttribute('aria-description', viewEditableRoot)
        }
      }
    })
  }, [ariaDescription, isEditorReady])

  const { value: zIndex } = useZIndexContext()

  const { features } = React.useContext(TextEditorContext)
  const enableStickyToolbar = !!features?.stickyToolbar
  const enableTabAsNavigation = !!features?.tabAsNavigation

  const imageUploadHandlers = React.useMemo(() => {
    if (companyId === undefined || toolName === undefined) {
      return undefined
    }

    const handlerOptions = {
      companyId,
      projectId,
      toolName,
      i18n,
    }

    const upload = richTextEditorUploadHandler(handlerOptions)

    return {
      upload,
      i18n,
      onError: showToast.error,
      onWarning: showImageUploadWarning,
    }
  }, [
    companyId,
    i18n,
    projectId,
    showImageUploadWarning,
    showToast.error,
    toolName,
  ])

  const mergedConfig = React.useMemo(() => {
    const defaultConfig = getDefaultConfig(locale, {
      imageUpload: imageUploadHandlers,
    })

    // Set accessible label for the editable area if provided
    // This is used by screen readers to announce the editor's purpose
    if (ariaLabel) {
      defaultConfig.label = ariaLabel
    }

    if (placeholder) {
      defaultConfig.root = { ...defaultConfig.root, placeholder }
    }

    return defaultConfig
  }, [ariaLabel, imageUploadHandlers, locale, placeholder])

  const bindKeyDownHandler = React.useCallback(
    (editor: ClassicEditor) => {
      const editorElement = editor.editing.view.document.getRoot()
      if (!editorElement) {
        return
      }

      // Remove existing keydown listener
      if (keyDownListenerRef.current) {
        editor.editing.view.document.off('keydown', keyDownListenerRef.current)
      }

      // Create and store the new listener
      const keyDownListener = (
        _event: EventInfo,
        data: { domEvent: Event }
      ) => {
        const domEvent = data.domEvent as KeyboardEvent
        // Prevent Enter from propagating to parent forms so the
        // editor can handle newlines without accidentally submitting.
        if (domEvent.key === 'Enter') {
          domEvent.stopPropagation()
        }

        onKeyDown?.(domEvent, editor)
      }

      keyDownListenerRef.current = keyDownListener

      // Add the keydown listener
      editor.editing.view.document.on('keydown', keyDownListener)
    },
    [onKeyDown]
  )

  const debouncedChange = React.useRef(
    debounce((_event: EventInfo<string, unknown>, editor: ClassicEditor) => {
      const data = editor.getData()
      const isDirty = data !== lastExternalValueRef.current.trim()
      const hasPendingUploads = hasPendingImageUploads(editor)

      if (isDirty && !isDirtyState) {
        setIsDirtyState(true)
        onDirty?.()
      }

      if (hasPendingUploads) {
        return
      }

      onChange?.(data, isDirty)
    }, DEBOUNCE_MS)
  ).current

  const handleChange = debouncedChange

  const handleReady = (editor: ClassicEditor) => {
    addButtonDataAttributes(editor)
    editorInstanceRef.current = editor
    setIsEditorReady(true)
    const sanitizedValue = sanitizeTextEditorHtml(value ?? initialValue ?? '')
    if (sanitizedValue !== editor.getData()) {
      editor.data.set(sanitizedValue)
    }

    // Set aria-label and aria-description on the editable area if provided
    // These provide accessible context to screen reader users
    editor.editing.view.change((writer) => {
      const viewEditableRoot = editor.editing.view.document.getRoot()
      if (viewEditableRoot) {
        if (ariaLabel) {
          writer.setAttribute('aria-label', ariaLabel, viewEditableRoot)
        }
        if (ariaDescription) {
          writer.setAttribute(
            'aria-description',
            ariaDescription,
            viewEditableRoot
          )
        }
        if (rows !== undefined) {
          const baseHeight = 35
          const rowHeight = 22
          const editorHeight = rows * rowHeight + baseHeight
          writer.setStyle('min-height', `${editorHeight}px`, viewEditableRoot)

          if (!extendable) {
            writer.setStyle('max-height', `${editorHeight}px`, viewEditableRoot)
          }
        }
      }
    })

    // Bind keydown handler when editor is ready
    bindKeyDownHandler(editor)

    onInit?.(editor)
  }

  const handleBlur = (
    event: EventInfo<string, unknown>,
    editor: ClassicEditor
  ) => {
    debouncedChange.flush()
    onBlur?.(event, editor)
  }

  useStickyToolbar({
    enabled: enableStickyToolbar,
    editor: isEditorReady ? editorInstanceRef.current : null,
    editorRef,
    cssLoading,
  })

  const { config } = useTabAsNavigation({
    config: mergedConfig,
    enabled: enableTabAsNavigation,
    editor: isEditorReady ? editorInstanceRef.current : null,
  })

  const editorKey = `${locale}-${enableTabAsNavigation}`

  if (cssLoading) {
    return null
  }

  if (readonly) {
    return (
      <TextEditorOutput
        value={value}
        aria-label={ariaLabel}
        aria-description={ariaDescription}
      />
    )
  }

  return (
    <StyledTextEditor
      ref={editorRef}
      key={editorKey}
      error={error}
      $ready={isEditorReady}
      aria-invalid={error ? 'true' : 'false'}
    >
      <GlobalEditorStyles zIndex={zIndex + 1} />
      <TextEditorTheme />
      <CKEditor
        {...restProps}
        editor={ClassicEditor}
        config={config}
        disabled={disabled}
        data={initialValueRef.current}
        onChange={handleChange}
        onReady={handleReady}
        onBlur={handleBlur}
      />
    </StyledTextEditor>
  )
}

function hasPendingImageUploads(editor: ClassicEditor) {
  try {
    const fileRepository = editor.plugins.get(FileRepository) as {
      loaders?: Iterable<{ status?: string }>
    }

    return Array.from(fileRepository.loaders ?? []).some((loader) =>
      ['reading', 'uploading'].includes(String(loader.status))
    )
  } catch (_error) {
    return false
  }
}
